import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { creativeAiApi, type CreativeAiScope } from '../api/CreativeAiApi'
import type { CreativeAiAnalysis, CreativeAiAnalysisDetail } from '../models/analysis.types'
import { ANALYSIS_ACTIVE_STATUSES } from '../models/analysis.types'
import type {
  CreativeLabellingAnalysisProgress,
  CreativeLabellingProgressEvent,
} from '../models/labelling.types'

import { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

const ACTIVE: ReadonlySet<string> = new Set(ANALYSIS_ACTIVE_STATUSES)

function asApiError(cause: unknown): ApiError {
  return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
}

function isCanceled(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.kind === 'canceled'
}

function sameScope(left: CreativeAiScope | null, right: CreativeAiScope): boolean {
  return (
    left !== null &&
    String(left.accountId) === String(right.accountId) &&
    String(left.workspaceId) === String(right.workspaceId)
  )
}

export const useCreativeAiStore = defineStore('creativeAi', () => {
  const analyses = ref<CreativeAiAnalysis[]>([])
  const imageLabelCount = ref(0)
  const videoLabelCount = ref(0)
  const listState = ref<UiAsyncState>('empty')
  /**
   * True once a list read has answered for this scope — the label counts are only meaningful then.
   * `listState` starts at `empty`, so it cannot tell "no labels" from "not read yet" (ORP-20490).
   */
  const labelsLoaded = ref(false)
  const listError = ref<ApiError | null>(null)
  const active = ref<CreativeAiAnalysisDetail | null>(null)
  const detailState = ref<UiAsyncState>('empty')
  const detailError = ref<ApiError | null>(null)
  const pendingAnalysisId = ref<number | null>(null)
  const progress = ref<Record<string, CreativeLabellingAnalysisProgress>>({})

  let scope: CreativeAiScope | null = null
  let listGeneration = 0
  let detailGeneration = 0

  const hasAnyLabels = computed(() => imageLabelCount.value + videoLabelCount.value > 0)
  const hasActiveRun = computed(() => analyses.value.some((row) => ACTIVE.has(row.status)))

  function labelCount(kind: 'image' | 'video'): number {
    return kind === 'image' ? imageLabelCount.value : videoLabelCount.value
  }

  function analysisById(id: number | string): CreativeAiAnalysis | undefined {
    return analyses.value.find((row) => String(row.id) === String(id))
  }

  function isActive(id: number | string): boolean {
    const row = analysisById(id)
    return row ? ACTIVE.has(row.status) : false
  }

  function progressFor(id: number | string): CreativeLabellingAnalysisProgress | undefined {
    return progress.value[String(id)]
  }

  async function loadAnalyses(
    nextScope: CreativeAiScope,
    options: { signal?: AbortSignal; silent?: boolean } = {},
  ): Promise<void> {
    const requestGeneration = ++listGeneration
    scope = nextScope
    if (!options.silent) listState.value = 'loading'
    listError.value = null
    try {
      const response = await creativeAiApi.listAnalyses(nextScope, options.signal)
      if (requestGeneration !== listGeneration || options.signal?.aborted) return
      analyses.value = response.analyses
      imageLabelCount.value = response.image_label_count
      videoLabelCount.value = response.video_label_count
      labelsLoaded.value = true
      listState.value = response.analyses.length === 0 ? 'empty' : 'ready'
    } catch (cause) {
      if (requestGeneration !== listGeneration || options.signal?.aborted || isCanceled(cause))
        return
      listError.value = asApiError(cause)
      listState.value = 'error'
    }
  }

  async function reloadAnalyses(): Promise<void> {
    if (!scope) return
    await loadAnalyses(scope, { silent: true })
  }

  async function loadAnalysis(
    nextScope: CreativeAiScope,
    id: number | string,
    options: { signal?: AbortSignal; silent?: boolean } = {},
  ): Promise<void> {
    const requestGeneration = ++detailGeneration
    scope = nextScope
    if (!options.silent) {
      detailState.value = 'loading'
      active.value = null
    }
    detailError.value = null
    try {
      const response = await creativeAiApi.getAnalysis(nextScope, id, options.signal)
      if (requestGeneration !== detailGeneration || options.signal?.aborted) return
      active.value = response
      upsertAnalysis(response)
      detailState.value = 'ready'
    } catch (cause) {
      if (requestGeneration !== detailGeneration || options.signal?.aborted || isCanceled(cause))
        return
      detailError.value = asApiError(cause)
      detailState.value = 'error'
    }
  }

  function upsertAnalysis(analysis: CreativeAiAnalysis): void {
    const index = analyses.value.findIndex((row) => row.id === analysis.id)
    if (index < 0) analyses.value.unshift(analysis)
    else analyses.value[index] = analysis
    if (analyses.value.length > 0 && listState.value === 'empty') listState.value = 'ready'
  }

  function patchAnalysis(id: number | string, patch: Partial<CreativeAiAnalysis>): void {
    const index = analyses.value.findIndex((row) => String(row.id) === String(id))
    const current = analyses.value[index]
    if (index >= 0 && current) analyses.value[index] = { ...current, ...patch }
    if (active.value && String(active.value.id) === String(id)) {
      active.value = { ...active.value, ...patch }
    }
  }

  function removeAnalysis(id: number | string): void {
    analyses.value = analyses.value.filter((row) => String(row.id) !== String(id))
    if (active.value && String(active.value.id) === String(id)) active.value = null
    delete progress.value[String(id)]
    if (analyses.value.length === 0 && listState.value === 'ready') listState.value = 'empty'
  }

  function applyAnalysisProgress(frame: CreativeLabellingProgressEvent): void {
    for (const analysisProgress of frame.analyses) {
      progress.value[String(analysisProgress.id)] = analysisProgress
      const linked = analysisById(analysisProgress.id)
      patchAnalysis(analysisProgress.id, {
        status: analysisProgress.status,
        ...(linked?.labeling_run === frame.id
          ? {
              labelled_count: frame.labelled_count,
              total_count: frame.total_count,
              skipped_count: frame.skipped_count,
              failed_count: frame.failed_count,
              failure_reason: frame.error_message,
            }
          : {}),
      })
    }
  }

  function markPending(id: number | null): void {
    pendingAnalysisId.value = id
  }

  function resetState(): void {
    listGeneration += 1
    detailGeneration += 1
    scope = null
    analyses.value = []
    imageLabelCount.value = 0
    videoLabelCount.value = 0
    labelsLoaded.value = false
    listState.value = 'empty'
    listError.value = null
    active.value = null
    detailState.value = 'empty'
    detailError.value = null
    pendingAnalysisId.value = null
    progress.value = {}
  }

  return {
    analyses,
    imageLabelCount,
    videoLabelCount,
    listState,
    labelsLoaded,
    listError,
    active,
    detailState,
    detailError,
    pendingAnalysisId,
    progress,
    hasAnyLabels,
    hasActiveRun,
    labelCount,
    analysisById,
    isActive,
    progressFor,
    loadAnalyses,
    reloadAnalyses,
    loadAnalysis,
    upsertAnalysis,
    patchAnalysis,
    removeAnalysis,
    applyAnalysisProgress,
    markPending,
    resetState,
    hasScope: (candidate: CreativeAiScope) => sameScope(scope, candidate),
  }
})

export type CreativeAiStore = ReturnType<typeof useCreativeAiStore>
